import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import {
  DEFAULT_PERIOD_WEEKS,
  PERIOD_PRESETS,
  Period,
  buildPeriod,
} from "../model/period"
import {
  dayKeyFromEpochDay,
  epochDayFromCivil,
  startOfIsoWeek,
  todayEpochDayMsk,
} from "../model/mskCalendar"

const FROM_RE = /^\d{4}-\d{2}-\d{2}$/

/** Чистый разбор URL-параметров — вынесен для тестов. */
export const parsePeriodParams = (
  search: string,
  now: number = Date.now(),
): { startEpochDay: number; weeks: number } => {
  const params = new URLSearchParams(search)
  const weeksRaw = Number(params.get("weeks"))
  const weeks = PERIOD_PRESETS.includes(weeksRaw)
    ? weeksRaw
    : DEFAULT_PERIOD_WEEKS
  let startEpochDay = startOfIsoWeek(todayEpochDayMsk(now))
  const fromRaw = params.get("from") ?? ""
  if (FROM_RE.test(fromRaw)) {
    const [y, m, d] = fromRaw.split("-").map(Number)
    const candidate = epochDayFromCivil(y, m, d)
    // Защита от "2026-13-45" и "2026-02-30": epochDayFromCivil арифметичен и молча
    // нормализует такие даты, поэтому round-trip обязан вернуть ту же строку.
    if (dayKeyFromEpochDay(candidate) === fromRaw) {
      startEpochDay = startOfIsoWeek(candidate)
    }
  }
  return { startEpochDay, weeks }
}

/** Сборка URL-параметров — значения по умолчанию не пишем, чтобы URL оставался чистым. */
export const buildPeriodParams = (
  startEpochDay: number,
  weeks: number,
  now: number = Date.now(),
): URLSearchParams => {
  const params = new URLSearchParams()
  if (startEpochDay !== startOfIsoWeek(todayEpochDayMsk(now))) {
    params.set("from", dayKeyFromEpochDay(startEpochDay))
  }
  if (weeks !== DEFAULT_PERIOD_WEEKS) {
    params.set("weeks", String(weeks))
  }
  return params
}

export interface UseSchedulePeriodResult {
  period: Period
  setWeeks: (weeks: number) => void
  shift: (deltaWeeks: number) => void
  /** Переход на неделю, содержащую сегодняшний день (начало периода — её понедельник). */
  goCurrentWeek: () => void
  setStart: (dateFromPicker: Date) => void
}

/**
 * Состояние периода живёт в URL (`?from=YYYY-MM-DD&weeks=4`), чтобы график можно было
 * переслать ссылкой. Разбор жёсткий: ссылку могут поправить руками.
 *
 * `replace: true` — перелистывание недель не должно засорять историю браузера.
 */
export const useSchedulePeriod = (): UseSchedulePeriodResult => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { startEpochDay, weeks } = parsePeriodParams(searchParams.toString())
  const period = useMemo(
    () => buildPeriod(startEpochDay, weeks),
    [startEpochDay, weeks],
  )

  const apply = useCallback(
    (start: number, w: number) =>
      setSearchParams(buildPeriodParams(start, w), { replace: true }),
    [setSearchParams],
  )

  const setWeeks = useCallback(
    (w: number) => apply(period.startEpochDay, w),
    [apply, period.startEpochDay],
  )
  const shift = useCallback(
    (deltaWeeks: number) =>
      apply(period.startEpochDay + deltaWeeks * 7, period.weeks),
    [apply, period.startEpochDay, period.weeks],
  )
  const goCurrentWeek = useCallback(
    () => apply(startOfIsoWeek(todayEpochDayMsk()), period.weeks),
    [apply, period.weeks],
  )
  // Из MUI DatePicker приходит Date в локальной зоне: пользователь выбрал день в календаре,
  // его wall-clock и есть намерение — читаем локальные поля, а не UTC (спека, раздел 4).
  const setStart = useCallback(
    (date: Date) =>
      apply(
        epochDayFromCivil(
          date.getFullYear(),
          date.getMonth() + 1,
          date.getDate(),
        ),
        period.weeks,
      ),
    [apply, period.weeks],
  )

  return { period, setWeeks, shift, goCurrentWeek, setStart }
}
